/* eslint-disable react/prop-types */
import { Tag, Col, Row, Form, Card, Select, Switch, Typography, InputNumber } from "antd";
import type { FormInstance } from "antd";
import type { FeaturesFormValues } from "../../hooks/useFeaturesForm";
import { FaCheckCircle, FaFileAlt, FaPercentage } from "react-icons/fa";
import useFooterStore from "views/hooks/useFooterStore";
import { useTranslation } from "react-i18next";
import { MdAutorenew } from "react-icons/md";
import React from "react";
import CardTitle from "components/CardTitle";
import LabelWithTooltip from "components/LabelWithTooltip";
import { IoMdAnalytics } from "react-icons/io";
import useGetAnalyticalTypesOptions from "../utils/useGetClientEnumOptions";
import useCoreStore from "views/hooks/useCoreStore";
import { FaCubesStacked } from "react-icons/fa6";

interface FeaturesFormPropsTypes {
  onFinish: (values: FeaturesFormValues) => Promise<void>;
  form: FormInstance;
  invoiceTypeOptions: globalThis.SaleDocumentType[];
  isSetup?: boolean;
}

export default function FeaturesForm(props: FeaturesFormPropsTypes): React.ReactElement {
  const { form, onFinish, invoiceTypeOptions, isSetup } = props;

  const { t } = useTranslation();
  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const companyData = useCoreStore(state => state.companyData);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);

  const { options } = useGetAnalyticalTypesOptions();
  const analyticalLevel = companyData?.analyticalLevel;
  const hasAnalyticalLevel = Number(analyticalLevel) > 0;
  const tagRender = (props: {
    label?: React.ReactNode;
    closable?: boolean;
    onClose?: (e: React.MouseEvent<HTMLElement>) => void;
  }) => {
    const { label, closable, onClose } = props;

    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={"#3e849e"}
        onMouseDown={onPreventMouseDown}
        style={{ marginRight: 3 }}
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
    );
  };

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  return (
    <Form
      name="updateCompanySettings"
      layout="vertical"
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title={<CardTitle icon={<FaFileAlt />} title={t("Display documents")} />}
                className="antd-newrai-card"
              >
                <Col span={24}>
                  <Form.Item
                    rules={[{ required: isSetup, message: t("This field is required") }]}
                    name="saleDocumentTypes"
                    label={t("Document types")}
                  >
                    <Select mode="multiple" tagRender={tagRender} options={invoiceTypeOptions} />
                  </Form.Item>
                </Col>
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title={
                  <CardTitle icon={<FaPercentage />} title={t("Default price type (HT/TTC)")} />
                }
                className="antd-newrai-card"
              >
                <Row>
                  <Col span={24}>
                    <Form.Item name="defaultPriceType" valuePropName="checked">
                      <Switch checkedChildren={t("TTC")} unCheckedChildren={t("HT")} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Typography.Paragraph
                      type="secondary"
                      style={{
                        marginBottom: 12,
                        padding: "8px 12px",
                        borderLeft: "3px solid #3e849e",
                        backgroundColor: "rgba(62, 132, 158, 0.05)",
                        borderRadius: "0 4px 4px 0",
                      }}
                    >
                      <strong>TTC :</strong> Cette option vous permet de facturer toutes taxes
                      comprises. Les prix affichés dans le catalogue produits seront en TTC. Le
                      système calculera automatiquement le montant hors taxe en fonction du taux de
                      TVA applicable.
                    </Typography.Paragraph>
                    <Typography.Paragraph
                      type="secondary"
                      style={{
                        marginBottom: 0,
                        padding: "8px 12px",
                        borderLeft: "3px solid #3e849e",
                        backgroundColor: "rgba(62, 132, 158, 0.05)",
                        borderRadius: "0 4px 4px 0",
                      }}
                    >
                      <strong>HT :</strong> Cette option vous permet de facturer hors taxes. Les
                      prix dans le catalogue produits seront affichés en HT, et la TVA sera ajoutée
                      lors du calcul final. Vous pourrez modifier ponctuellement le prix HT pour
                      chaque article si nécessaire.
                    </Typography.Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title={<CardTitle icon={<MdAutorenew />} title={t("Automations")} />}
                className="antd-newrai-card"
              >
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="clientHasAutoNumber"
                      valuePropName="checked"
                      label={
                        <LabelWithTooltip
                          labelTitle={t("Client automatic numbering")}
                          infoTooltipTitle={t(
                            "If enabled, client numbering will be automatically generated.",
                          )}
                        />
                      }
                    >
                      <Switch checkedChildren={t("Yes")} unCheckedChildren={t("No")} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="sendOverdueEmail"
                      valuePropName="checked"
                      label={
                        <LabelWithTooltip
                          labelTitle={t("Automatic reminder")}
                          infoTooltipTitle={t(
                            "If enabled, automatic reminders will be sent to clients for unpaid invoices.",
                          )}
                        />
                      }
                    >
                      <Switch checkedChildren={t("Yes")} unCheckedChildren={t("No")} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
            {hasAnalyticalLevel && (
              <Col span={12}>
                <Card
                  title={<CardTitle icon={<IoMdAnalytics />} title={t("Analytical type")} />}
                  className="antd-newrai-card"
                >
                  <Row>
                    <Col span={24}>
                      <Form.Item name="analyticalType" label={t("Analytical type")}>
                        <Select options={options} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            )}
            <Col span={12}>
              <Card
                title={
                  <CardTitle icon={<FaCubesStacked />} title={t("Counterpart allocation type")} />
                }
                className="antd-newrai-card"
              >
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="counterpartProductAllocationType"
                      label={t("Counterpart allocation type")}
                    >
                      <Select
                        options={[
                          { label: t("Service"), value: "by_service" },
                          { label: t("Category"), value: "by_category" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={12}>
              <Card
                title={<CardTitle icon={<FaCheckCircle />} title={t("Terms of validity")} />}
                className="antd-newrai-card"
              >
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="availabilityTime"
                      label={t("Duration of validity in days (Budget and Orders)")}
                    >
                      <InputNumber maxLength={3} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
}
